import React from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, FileCheck, Megaphone, Newspaper, Calendar, Hammer,
  Award, Users, Download, LogOut, Building2, ExternalLink,
} from 'lucide-react';
import { auth } from '@/lib/auth';
import { BARANGAY, BRAND } from '@/data/mockData';

const NAV = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { section: 'Operations' },
  { to: '/admin/requests', icon: FileCheck, label: 'Service Requests' },
  { to: '/admin/concerns', icon: Megaphone, label: 'Concern Reports' },
  { section: 'Content Management' },
  { to: '/admin/content/announcements', icon: Newspaper, label: 'Announcements' },
  { to: '/admin/content/programs', icon: Calendar, label: 'Programs' },
  { to: '/admin/content/projects', icon: Hammer, label: 'Projects' },
  { to: '/admin/content/accomplishments', icon: Award, label: 'Accomplishments' },
  { to: '/admin/content/officials', icon: Users, label: 'Officials' },
  { section: 'Reports' },
  { to: '/admin/export', icon: Download, label: 'Export Reports' },
];

export const AdminSidebar = ({ open, onClose }) => {
  const nav = useNavigate();
  const user = auth.user();

  const handleLogout = () => {
    auth.logout();
    nav('/admin/login');
  };

  return (
    <>
      {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={onClose} />}
      <aside
        className={`fixed lg:sticky top-0 z-50 lg:z-10 flex h-screen w-72 flex-col bg-primary-deep text-primary-foreground transition-transform ${
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="border-b border-white/10 px-5 py-5">
          <Link to="/admin" className="flex items-center gap-3">
            <img src={BRAND.logoUrl} alt="logo" className="h-10 w-10 rounded-full bg-white/95 p-0.5" />
            <div className="leading-tight">
              <div className="font-display text-sm font-bold">{BARANGAY.name}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Admin Portal</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 overflow-auto px-3 py-4 scrollbar-thin">
          {NAV.map((item, idx) => {
            if (item.section) {
              return (
                <div key={`s-${idx}`} className="mt-5 first:mt-0 px-3 text-[10px] font-bold uppercase tracking-[0.18em] opacity-60">
                  {item.section}
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onClose}
                className={({ isActive }) =>
                  `mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive ? 'bg-white/15 text-white' : 'text-white/75 hover:bg-white/10 hover:text-white'
                  }`
                }
                data-testid={`admin-nav-${item.label.replace(/\s/g, '-').toLowerCase()}`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-4 space-y-3">
          <div className="rounded-xl bg-white/5 px-3 py-2.5">
            <div className="text-xs uppercase tracking-widest opacity-70">Signed in</div>
            <div className="text-sm font-semibold mt-0.5">{user?.displayName || 'Administrator'}</div>
            <div className="text-[11px] opacity-70">{user?.role}</div>
          </div>
          <Button asChild variant="outline" className="w-full bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white" size="sm" data-testid="admin-view-public">
            <Link to="/" target="_blank"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> View Public Site</Link>
          </Button>
          <Button onClick={handleLogout} variant="ghost" className="w-full text-white/80 hover:bg-destructive/20 hover:text-destructive" size="sm" data-testid="admin-logout">
            <LogOut className="h-3.5 w-3.5 mr-1.5" /> Log out
          </Button>
        </div>
      </aside>
    </>
  );
};
